// src/components/Admin/ItemManager/ItemFormDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControlLabel, Checkbox, Box,
  Typography, FormControl, FormLabel, RadioGroup, Radio,
} from '@mui/material';
import api from '../../../api/axios';

// 안전한 이미지 경로 처리
const getFullImageUrl = (url) =>
  url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

export default function ItemFormDialog({
  open, onClose, item, onSaved,
}) {
  const isEdit = Boolean(item);

  /* ─── state ─── */
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');
  const [isUnlimited, setIsUnlimited] = useState(true);
  const [isCoupon, setIsCoupon] = useState(true);
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);

  /* ─── 초기값 ─── */
  useEffect(() => {
    if (item) {
      setName(item.name || '');
      setPrice(item.price || '');
      setStock(item.stock || '');
      setIsUnlimited(item.is_unlimited ?? true);
      setIsCoupon(item.is_coupon ?? true);
      setDescription(item.description || '');
    } else {
      setName(''); setPrice(''); setStock(''); setIsUnlimited(true);
      setIsCoupon(true); setDescription('');
    }
    setImage(null);
  }, [item, open]);

  /* ─── 저장 ─── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('name', name);
    fd.append('price', parseInt(price || 0));
    fd.append('is_unlimited', isUnlimited ? 'true' : 'false');
    fd.append('stock', isUnlimited ? 0 : parseInt(stock || 0));
    fd.append('is_coupon', isCoupon ? 'true' : 'false');
    fd.append('description', description);
    fd.append('is_active', 'true');
    if (image) fd.append('image', image);

    try {
      if (isEdit) {
        await api.post(`/api/rewards/item-admin/${item.id}/update/`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/api/rewards/item-admin/', fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert('저장 실패');
    }
  };

  const disabled = !name.trim() || !price || (!isUnlimited && !stock);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          /* 모바일: 전체 90 % 축소 */
          '@media (max-width:600px)': {
            transform: 'scale(0.9)',
            transformOrigin: 'top left',
          },
        },
      }}
    >
      <DialogTitle>{isEdit ? '아이템 수정' : '아이템 등록'}</DialogTitle>

      <DialogContent dividers>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
        >
          {/* 기본 입력 */}
          <TextField
            label="아이템 이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <TextField
            label="가격(Leaf)"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
          />

          {/* 수량 · 무제한 */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isUnlimited}
                onChange={(e) => setIsUnlimited(e.target.checked)}
              />
            }
            label="무제한"
          />
          <TextField
            label="수량"
            type="number"
            value={stock}
            onChange={(e) => setStock(e.target.value)}
            disabled={isUnlimited}
            required={!isUnlimited}
          />

          {/* 타입 선택 */}
          <FormControl>
            <FormLabel>아이템 타입</FormLabel>
            <RadioGroup
              row
              value={isCoupon ? 'coupon' : 'normal'}
              onChange={(e) => setIsCoupon(e.target.value === 'coupon')}
            >
              <FormControlLabel value="coupon" control={<Radio />} label="쿠폰" />
              <FormControlLabel value="normal" control={<Radio />} label="일반" />
            </RadioGroup>
          </FormControl>

          {/* 설명 */}
          <TextField
            label="설명"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* 이미지 업로드 */}
          <Button variant="outlined" component="label">
            이미지 업로드
            <input type="file" hidden onChange={(e) => setImage(e.target.files[0])} />
          </Button>

          {/* 미리보기 */}
          <Box
            sx={{
              minWidth: 80, maxWidth: 100, height: 100,
              border: '1px dashed #ccc', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              borderRadius: 1,
            }}
          >
            {image ? (
              <img
                src={URL.createObjectURL(image)}
                alt="미리보기"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            ) : item?.image ? (
              <img
                src={getFullImageUrl(item.image)}
                alt="기존 이미지"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                미리보기
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={disabled}>
          {isEdit ? '수정' : '등록'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
